import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Building2, CalendarDays, Trophy } from "lucide-react";
import api from "lib/api";
import { useI18n } from "i18n";
import {
  EventCard,
  EventGridSkeleton,
  EmptyState,
} from "components/public/EventCard";
import { championshipLabel } from "lib/events";
import { usePageMeta } from "lib/meta";

/**
 * Pagina publica do campeonato: a competicao e as etapas dela.
 *
 * A URL aceita o slug (`/championships/copa-verao-2026`) ou o UUID — o backend
 * resolve os dois. So aparecem campeonatos com ao menos um evento publicado;
 * qualquer outro devolve 404 (ver `views/public.py:listed_championships`).
 */
export default function PublicChampionship() {
  const { key } = useParams();
  const { t, tf, lang } = useI18n();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await api.get(`/api/public/championships/${key}/`);
      setData(res.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    load();
  }, [load]);

  const item = data?.championship;
  usePageMeta(
    item && {
      title: championshipLabel(item),
      description:
        item.description ||
        [championshipLabel(item), item.organizer].filter(Boolean).join(" - "),
      image: item.cover_url,
    }
  );

  if (loading) {
    return (
      <section className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6">
        <EventGridSkeleton />
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6">
        <EmptyState
          title={t("championship.notFound")}
          hint={t("public.championship.notFoundHint")}
          action={
            <Link
              to="/"
              className="rounded-full bg-amber-500 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-600"
            >
              {t("public.championship.backHome")}
            </Link>
          }
        />
      </section>
    );
  }

  const { championship, events } = data;

  return (
    <>
      <section className="border-b border-neutral-200 bg-gradient-to-br from-brand-wash via-white to-white">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
          <Link
            to="/"
            className="mb-6 inline-flex items-center gap-2 text-sm text-neutral-500 transition-colors hover:text-neutral-900"
          >
            <ArrowLeft size={16} />
            {t("public.championship.backHome")}
          </Link>

          <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
            {championship.cover_url ? (
              <img
                src={championship.cover_url}
                alt={championship.name}
                className="h-24 w-24 shrink-0 rounded-2xl object-cover shadow-sm"
              />
            ) : (
              <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-600">
                <Trophy size={36} />
              </div>
            )}

            <div className="min-w-0">
              <h1 className="text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
                {championship.name}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-600">
                {championship.season && (
                  <span className="flex items-center gap-1.5">
                    <CalendarDays size={14} />
                    {championship.season}
                  </span>
                )}
                {championship.organizer && (
                  <span className="flex items-center gap-1.5">
                    <Building2 size={14} />
                    {championship.organizer}
                  </span>
                )}
                <span className="flex items-center gap-1.5">
                  <Trophy size={14} />
                  {tf("public.championship.eventsCount", {
                    count: championship.event_count,
                  })}
                </span>
              </div>
              {championship.description && (
                <p className="mt-3 max-w-2xl whitespace-pre-line text-base text-neutral-600">
                  {championship.description}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6">
        {events.length === 0 ? (
          <EmptyState
            title={t("public.championship.empty")}
            hint={t("public.home.emptyHint")}
          />
        ) : (
          <>
            <h2 className="mb-5 text-lg font-semibold text-neutral-900">
              {t("public.championship.eventsTitle")}
              <span className="ml-2 text-sm font-normal text-neutral-500">
                {events.length}
              </span>
            </h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {events.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  lang={lang}
                  t={t}
                  showChampionship={false}
                />
              ))}
            </div>
          </>
        )}
      </section>
    </>
  );
}
